import {
  Filter,
  TiVoAppProvider,
  TiVoAppVariant,
  TiVoPublicationHistory,
  TiVoSnapshotStatus,
} from "hooks/tivoApps/types";
import {
  TiVoDeviceClientType,
  TiVoDeviceOem,
  TiVoDevicePlatform,
} from "hooks/tivoDevices";
import { API_URLS } from "src/config";
import { JsonApiRequestBody } from "types/api";

import { METHOD_NAMES } from "../constant";
import { QueryHydrationContext, QueryParams } from "../types";
import { defineEndpoint } from "./defineEndpoint";

type ProviderWithIncludes = {
  id: string;
  variants?: unknown[];
};

export const protoQueryKeys = {
  tivoApps: {
    providers: (queryParams?: QueryParams) =>
      ["tivoApps", "providers", queryParams] as const,
    providersFilters: (queryParams?: QueryParams) =>
      ["tivoApps", "providersFilters", queryParams] as const,
    provider: (providerId?: string, queryParams?: QueryParams) =>
      ["tivoApps", "provider", providerId, queryParams] as const,
    variants: (providerId?: string, queryParams?: QueryParams) =>
      ["tivoApps", "variants", providerId, queryParams] as const,
    variantPublicationHistory: (
      variantId?: string,
      queryParams?: QueryParams
    ) =>
      [
        "tivoApps",
        "variantPublicationHistory",
        variantId,
        queryParams,
      ] as const,
  },
  tivoDevices: {
    oem: (platformId?: string, oemId?: string, queryParams?: QueryParams) =>
      ["tivoDevices", "oem", platformId, oemId, queryParams] as const,
    platforms: (queryParams?: QueryParams) =>
      ["tivoDevices", "platforms", queryParams] as const,
  },
};

export const tivoAppsEndpoints = {
  // Tivo Apps
  getTiVoProviders: defineEndpoint<TiVoAppProvider[]>()({
    method: METHOD_NAMES.GET,
    path: () => `${API_URLS.TIVO_APPS}/providers`,
    getQueryKey: (_args: [], queryParams?: QueryParams) =>
      protoQueryKeys.tivoApps.providers(queryParams),
    hydrateQueryCache: ({
      data,
      queryClient,
      queryParams,
    }: QueryHydrationContext<[], ProviderWithIncludes[]>) => {
      if (!Array.isArray(data.results)) {
        return;
      }

      data.results.forEach((providerResult: ProviderWithIncludes) => {
        const provider = providerResult;
        queryClient.setQueryData(
          protoQueryKeys.tivoApps.provider(provider.id),
          {
            results: provider,
          }
        );
        queryClient.setQueryData(
          protoQueryKeys.tivoApps.provider(provider.id, queryParams),
          {
            results: provider,
          }
        );

        if (provider.variants) {
          queryClient.setQueryData(
            protoQueryKeys.tivoApps.variants(provider.id),
            {
              results: provider.variants,
            }
          );
        }
      });
    },
  }),
  getTiVoProvidersFilters: defineEndpoint<Filter[]>()({
    method: METHOD_NAMES.GET,
    path: () => `${API_URLS.TIVO_APPS}/providers/filters`,
    getQueryKey: (_args: [], queryParams?: QueryParams) =>
      protoQueryKeys.tivoApps.providersFilters(queryParams),
  }),
  getTiVoProvider: defineEndpoint<TiVoAppProvider>()({
    method: METHOD_NAMES.GET,
    path: (providerId: string) =>
      `${API_URLS.TIVO_APPS}/providers/${providerId}`,
    getQueryKey: ([providerId]: [string], queryParams?: QueryParams) =>
      protoQueryKeys.tivoApps.provider(providerId, queryParams),
    hydrateQueryCache: ({
      data,
      queryClient,
      args,
      queryParams,
    }: QueryHydrationContext<[string], ProviderWithIncludes>) => {
      const providerId = args[0];
      const provider = data.results as ProviderWithIncludes;

      queryClient.setQueryData(protoQueryKeys.tivoApps.provider(providerId), {
        results: provider,
      });
      queryClient.setQueryData(
        protoQueryKeys.tivoApps.provider(providerId, queryParams),
        {
          results: provider,
        }
      );

      if (provider?.variants) {
        queryClient.setQueryData(protoQueryKeys.tivoApps.variants(providerId), {
          results: provider.variants,
        });
      }
    },
  }),
  getTiVoVariants: defineEndpoint<TiVoAppVariant[]>()({
    method: METHOD_NAMES.GET,
    path: (providerId: string) =>
      `${API_URLS.TIVO_APPS}/providers/${providerId}/variants`,
    getQueryKey: ([providerId]: [string], queryParams?: QueryParams) =>
      protoQueryKeys.tivoApps.variants(providerId, queryParams),
    hydrateQueryCache: ({
      data,
      queryClient,
      args,
    }: QueryHydrationContext<[string], unknown[]>) => {
      const providerId = args[0];
      queryClient.setQueryData(protoQueryKeys.tivoApps.variants(providerId), {
        results: data.results,
      });
    },
  }),
  getTiVoVariantPublicationHistory: defineEndpoint<TiVoPublicationHistory>()({
    method: METHOD_NAMES.GET,
    path: (variantId: string) =>
      `${API_URLS.TIVO_APPS}/variants/${variantId}/publication-history`,
    getQueryKey: ([variantId]: [string], queryParams?: QueryParams) =>
      protoQueryKeys.tivoApps.variantPublicationHistory(variantId, queryParams),
  }),
  getSnapshotStatus: defineEndpoint<TiVoSnapshotStatus>()({
    method: METHOD_NAMES.GET,
    path: () => `${API_URLS.TIVO_APPS}/snapshot`,
  }),

  // Tivo Devices
  getTiVoPlatforms: defineEndpoint<TiVoDevicePlatform[]>()({
    method: METHOD_NAMES.GET,
    path: () => `${API_URLS.TIVO_APPS}/platforms`,
    getQueryKey: (_args: [], queryParams?: QueryParams) =>
      protoQueryKeys.tivoDevices.platforms(queryParams),
  }),
  getTiVoOem: defineEndpoint<TiVoDeviceOem>()({
    method: METHOD_NAMES.GET,
    path: (platformId: string, oemId: string) =>
      `${API_URLS.TIVO_APPS}/platforms/${platformId}/oems/${oemId}`,
    getQueryKey: (
      [platformId, oemId]: [string, string],
      queryParams?: QueryParams
    ) => protoQueryKeys.tivoDevices.oem(platformId, oemId, queryParams),
  }),
  getTiVoClientType: defineEndpoint<TiVoDeviceClientType>()({
    method: METHOD_NAMES.GET,
    path: (platformId: string, oemId: string, clientTypeId: string) =>
      `${API_URLS.TIVO_APPS}/platforms/${platformId}/oems/${oemId}/client_types/${clientTypeId}`,
  }),
  createTiVoOem: defineEndpoint<
    TiVoDeviceOem,
    JsonApiRequestBody<Partial<Omit<TiVoDeviceOem, "id">>>
  >()({
    method: METHOD_NAMES.POST,
    path: (platformId: string) =>
      `${API_URLS.TIVO_APPS}/platforms/${platformId}/oems`,
  }),
  updateTiVoOem: defineEndpoint<
    TiVoDeviceOem,
    JsonApiRequestBody<Partial<TiVoDeviceOem>>
  >()({
    method: METHOD_NAMES.PUT,
    path: (platformId: string, oemId: string) =>
      `${API_URLS.TIVO_APPS}/platforms/${platformId}/oems/${oemId}`,
    invalidateQueries: ([platformId, oemId]: [string, string]) => [
      protoQueryKeys.tivoDevices.oem(platformId, oemId),
    ],
  }),
} as const;
